import { ChangeDetectionStrategy, Component, computed, inject, input, output, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PlotlyModule } from 'angular-plotly.js';
import { ProductionBatch, Scan, Lab } from '../../../model';
import { AxisSpans } from './batch-analysis.component';
import { ModelState } from '../../../store/model/model.state';

const LOG = '[PredictedAB]';

/**
 * Normalised threshold offsets (relative to target color).
 * Same shape as in the 3D plot so callers can share threshold data.
 */
interface Thresholds {
  lMin: number; lMax: number;
  aMin: number; aMax: number;
  bMin: number; bMax: number;
}

/**
 * 2-D scatter plot of **predicted** a* vs b* values for every scan in a batch.
 *
 * Why a*-b* specifically?
 *   The a*-b* plane is the chroma (hue/saturation) plane of LAB space.
 *   Most colour tolerance failures in manufacturing are chroma shifts, so
 *   this view lets operators instantly see drift in hue direction without
 *   needing to rotate a 3-D scene.
 *
 * Overlaid traces (all optional, driven by the inspection model):
 *  - Blue X         — target colour anchor
 *  - Blue circle    — ΔE tolerance radius projected onto the a*-b* plane.
 *                     NOTE: This is a 2-D projection, not the full sphere.
 *                     A point outside this circle MAY still pass if its L*
 *                     deviation is within tolerance — use the 3-D view for
 *                     the authoritative envelope.
 *  - Green rectangle — per-axis (da/db) tolerance box in the a*-b* plane
 */
@Component({
  selector: 'app-batch-predicted-ab-plot',
  imports: [CommonModule, PlotlyModule],
  template: `
    <div class="ab-plot-container">
      <plotly-plot
        [data]="plotData()"
        [layout]="plotLayout()"
        [config]="plotConfig()"
        (plotlyClick)="onPointClick($event)"
        [style]="{ width: '100%', height: '100%' }"
      ></plotly-plot>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
      isolation: isolate;
      border: 1px solid #d9d9d9;
    }
    .ab-plot-container {
      width: 100%;
      height: 600px;
    }
  `],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BatchPredictedAbPlotComponent {
  batch = input.required<ProductionBatch>();

  /**
   * Shared axis spans from the parent so this chart can be axis-aligned with
   * the measured/3-D charts when rendered side-by-side.
   * When null the component auto-calculates from its own data.
   */
  axisSpans = input<AxisSpans | null>(null);

  /**
   * Show the ΔE circle even when the model's primary mode is per-axis thresholds.
   * Keeps the ΔE context visible regardless of which pass/fail rule is active.
   */
  useDeltaE = input<boolean>(true);

  /**
   * Show the per-axis threshold rectangle even when the model is in ΔE mode.
   * Helps operators understand both tolerance shapes at once.
   */
  useThresholds = input<boolean>(true);

  scanSelected = output<Scan>();

  // ModelState injected globally — the inspection model is not batch-specific input.
  private modelState = inject(ModelState);

  /** LAB aim point from the batch's catalog color entry. Null when color data is absent. */
  targetColor = computed<Lab | null>(() => this.batch().color?.lab ?? null);

  /**
   * Inspection model ID taken from the first scan that carries one.
   * All scans in a batch use the same model — we just need any one.
   */
  inspectionModelId = computed<string | null>(() => {
    const scans = this.batch().scans ?? [];
    return scans.find(s => s.inspectionModelId != null)?.inspectionModelId ?? null;
  });

  /** Resolved inspection model, or null when no scan references one. */
  inspectionModel = computed(() => {
    const id = this.inspectionModelId();
    if (!id) {
      console.debug(`${LOG} No inspectionModelId — tolerance overlays hidden`);
      return null;
    }
    const m = this.modelState.all.find(m => m.id === id) ?? null;
    if (!m) console.warn(`${LOG} inspectionModelId ${id} not found in ModelState.all`);
    return m;
  });

  /**
   * ΔE threshold radius (LAB units) for the projected circle.
   *
   * Shown when the model is in ΔE mode OR when useDeltaE input is true.
   */
  deltaE = computed<number | null>(() => {
    const m = this.inspectionModel();
    if (!m) return null;
    if (!m.isUsingDeltaE && !this.useDeltaE()) return null;
    const de = m.deThreshold ?? null;
    console.debug(`${LOG} deltaE threshold`, de, '(model isUsingDeltaE:', m.isUsingDeltaE, ')');
    return de;
  });

  /**
   * Per-axis tolerance offsets from the target.
   *
   * Shown when the model is NOT in ΔE mode OR when useThresholds input is true.
   * Returns null when any of the six threshold values is missing — a partial
   * rectangle would give a misleading visual.
   */
  thresholds = computed<Thresholds | null>(() => {
    const m = this.inspectionModel();
    if (!m) return null;
    if (m.isUsingDeltaE && !this.useThresholds()) return null;

    const { dlMinThreshold, dlMaxThreshold, daMinThreshold, daMaxThreshold, dbMinThreshold, dbMaxThreshold } = m;
    if (dlMinThreshold == null || dlMaxThreshold == null ||
        daMinThreshold == null || daMaxThreshold == null ||
        dbMinThreshold == null || dbMaxThreshold == null) {
      console.warn(`${LOG} Incomplete threshold values on model ${m.id} — threshold rect hidden`);
      return null;
    }

    return {
      lMin: dlMinThreshold, lMax: dlMaxThreshold,
      aMin: daMinThreshold, aMax: daMaxThreshold,
      bMin: dbMinThreshold, bMax: dbMaxThreshold,
    };
  });

  /**
   * Auto-calculated a*-b* axis spans so every relevant point is visible.
   * Falls back to parent-supplied axisSpans (.a / .b fields) for alignment.
   *
   * Includes: scan predictions, target, ΔE circle extents, threshold box extents.
   * +2 = 1 unit pad on each side so the outermost geometry isn't clipped.
   */
  effectiveAxisSpans = computed<{ a: number; b: number }>(() => {
    const shared = this.axisSpans();
    if (shared) {
      console.debug(`${LOG} Using parent-supplied axisSpans a=${shared.a} b=${shared.b}`);
      return { a: shared.a, b: shared.b };
    }

    const aVals: number[] = [], bVals: number[] = [];
    const push = (a: number, b: number) => { aVals.push(a); bVals.push(b); };

    const target = this.targetColor();
    if (target) push(target.a, target.b);

    for (const s of this.batch().scans ?? []) {
      if (s.predicted) push(s.predicted.a, s.predicted.b);
    }

    // Circle extends equally on both axes — push the four axis-aligned extremes
    const de = this.deltaE();
    if (target && de != null) {
      push(target.a + de, target.b + de);
      push(target.a - de, target.b - de);
    }

    // Threshold box corners differ per axis
    const thr = this.thresholds();
    if (target && thr) {
      aVals.push(target.a + thr.aMin, target.a + thr.aMax);
      bVals.push(target.b + thr.bMin, target.b + thr.bMax);
    }

    const span = (vals: number[]) => vals.length ? Math.max(...vals) - Math.min(...vals) + 2 : 10;
    const spans = { a: span(aVals), b: span(bVals) };
    console.debug(`${LOG} Auto-computed axisSpans`, spans);
    return spans;
  });

  /**
   * All Plotly traces for the 2-D a*-b* scatter.
   *
   * Trace order: tolerance geometry first (background), scatter points on top.
   */
  plotData = computed(() => {
    const b = this.batch();
    if (!b?.scans) {
      console.warn(`${LOG} plotData: batch has no scans`);
      return [];
    }

    // Only include scans that carry a model prediction
    const scans = b.scans.filter(s => !!s.predicted);
    console.debug(`${LOG} plotData: ${scans.length} / ${b.scans.length} scans have predictions`);

    const x = scans.map(s => s.predicted!.a);  // a* on X axis (left-right chroma)
    const y = scans.map(s => s.predicted!.b);  // b* on Y axis (yellow-blue chroma)

    const text = scans.map(s => {
      const lab = s.predicted!;
      return `Scan: ${s.sequenceInBatch}<br>Status: ${s.state}<br>a*: ${lab.a.toFixed(2)}<br>b*: ${lab.b.toFixed(2)}<br>L*: ${lab.l.toFixed(2)}`;
    });

    // Green = Passed, Red = Failed, Grey = Ignored, Blue = Scanning, Amber = unknown
    const getColor = (s: Scan) => {
      switch (s.state) {
        case 'Passed':   return '#28a745';
        case 'Failed':   return '#dc3545';
        case 'Ignored':  return '#6c757d';
        case 'Scanning': return '#007bff';
        default:         return '#ffc107';
      }
    };

    const traces: object[] = [];
    const target = this.targetColor();

    // ── Tolerance geometry (drawn behind scatter points) ─────────────────────

    // ΔE circle: the radius projected onto the a*-b* plane.
    const de = this.deltaE();
    if (target && de != null && de > 0) {
      console.debug(`${LOG} Adding deltaE circle r=${de}`);
      traces.push(buildCircleTrace(target, de, 'rgba(100,160,255,0.35)', 'rgba(100,160,255,0.15)'));
    }

    // Per-axis threshold rectangle in the a*-b* plane (L* axis not shown in 2-D)
    const thr = this.thresholds();
    if (target && thr) {
      console.debug(`${LOG} Adding threshold rectangle`, thr);
      traces.push(buildRectTrace(target, thr, 'rgba(144,238,144,0.4)', 'rgba(144,238,144,0.15)'));
    }

    // ── Scan scatter points ──────────────────────────────────────────────────

    traces.push({
      x, y,
      mode: 'markers',
      type: 'scatter',
      text,
      hoverinfo: 'text',
      // customdata holds scan IDs for click-through navigation
      customdata: scans.map(s => s.id),
      marker: {
        size: 8,
        color: scans.map(s => getColor(s)),
        opacity: 0.85,
        line: { color: 'white', width: 0.5 },
      },
    });

    // Target color marker: drawn last so it sits on top of everything
    if (target) {
      traces.push({
        x: [target.a],
        y: [target.b],
        mode: 'markers',
        type: 'scatter',
        hoverinfo: 'text',
        text: [`Target a*: ${target.a.toFixed(2)}<br>b*: ${target.b.toFixed(2)}<br>L*: ${target.l.toFixed(2)}`],
        marker: {
          symbol: 'x',
          size: 12,
          color: '#0000ff',
          opacity: 1.0,
          line: { color: '#0000ff', width: 2 },
        },
      });
    }

    return traces;
  });

  /**
   * Layout for the 2-D a*-b* plot.
   *
   * Uses scaleanchor + scaleratio to keep a* and b* units equal on screen —
   * critical so the ΔE circle appears round rather than elliptical.
   * A squashed circle would mislead operators about the actual tolerance shape.
   */
  plotLayout = computed(() => {
    const data = this.plotData();
    const spans = this.effectiveAxisSpans();

    const allX: number[] = [];
    const allY: number[] = [];

    for (const trace of data as { x?: number[]; y?: number[] }[]) {
      if (Array.isArray(trace.x)) allX.push(...trace.x);
      if (Array.isArray(trace.y)) allY.push(...trace.y);
    }

    /**
     * Centers the axis range on the data midpoint and applies the supplied span.
     * Fixed span prevents the view from jumping when new scans arrive mid-batch.
     */
    const centeredRange = (values: number[], sharedSpan: number) => {
      if (!values.length) return undefined;
      const min = Math.min(...values);
      const max = Math.max(...values);
      const center = (min + max) / 2;
      const half = sharedSpan / 2;
      const pad = sharedSpan * 0.1; // 10 % breathing room
      return [center - half - pad, center + half + pad];
    };

    const rangeX = centeredRange(allX, spans.a);
    const rangeY = centeredRange(allY, spans.b);

    console.debug(`${LOG} plotLayout a*:`, rangeX, 'b*:', rangeY);

    return {
      xaxis: {
        title: { text: 'a*' },
        range: rangeX,
        // scaleanchor keeps a*-b* aspect ratio 1:1 so the circle stays round
        scaleanchor: 'y',
        scaleratio: 1,
        zeroline: true,
        zerolinecolor: '#cccccc',
      },
      yaxis: {
        title: { text: 'b*' },
        range: rangeY,
        zeroline: true,
        zerolinecolor: '#cccccc',
      },
      margin: { l: 50, r: 20, b: 50, t: 20 },
      showlegend: false,
      // Dark background between axes helps discriminate plot area from page
      plot_bgcolor: '#f8f9fa',
    };
  });

  plotConfig = computed(() => ({
    responsive: true,
    displayModeBar: true,
    scrollZoom: true,
  }));

  /**
   * Translates a Plotly click event into a typed Scan emission.
   * customdata on each point holds the scan ID set at trace-build time.
   */
  onPointClick(event: unknown) {
    const point = (event as { points?: { customdata?: string }[] }).points?.[0];
    if (!point?.customdata) return;

    const scan = this.batch().scans.find(s => s.id === point.customdata);
    if (scan) {
      console.debug(`${LOG} Scan clicked: id=${scan.id} seq=${scan.sequenceInBatch} state=${scan.state}`);
      this.scanSelected.emit(scan);
    }
  }
}

/**
 * Builds a filled Plotly scatter circle trace centered on `center` with radius `r`
 * in the a*-b* plane.
 *
 * Why a scatter trace instead of a Plotly shape?
 *   Plotly shapes (layout.shapes) use pixel or axis-fraction coordinates and do
 *   not respect the scaleanchor constraint — they would appear elliptical when
 *   a* and b* have different data ranges.  A scatter trace with `fill:'toself'`
 *   lives in data space and is correctly scaled by scaleanchor.
 *
 * @param center  LAB target color (only .a and .b are used)
 * @param r       Circle radius in LAB units
 * @param lineColor  Stroke color (CSS rgba)
 * @param fillColor  Fill color (CSS rgba, semi-transparent)
 */
function buildCircleTrace(center: Lab, r: number, lineColor: string, fillColor: string): object {
  // 64 segments give a visually smooth circle without excessive data points
  const steps = 64;
  const circleX: number[] = [];
  const circleY: number[] = [];

  for (let i = 0; i <= steps; i++) {
    const theta = (2 * Math.PI * i) / steps;
    circleX.push(center.a + r * Math.cos(theta));
    circleY.push(center.b + r * Math.sin(theta));
  }

  return {
    x: circleX,
    y: circleY,
    mode: 'lines',
    type: 'scatter',
    fill: 'toself',       // close and fill the polygon
    fillcolor: fillColor,
    line: { color: lineColor, width: 1.5 },
    hoverinfo: 'none',
  };
}

/**
 * Builds a filled Plotly scatter rectangle trace representing the per-axis
 * (da*, db*) tolerance zone in the a*-b* plane.
 *
 * The L* dimension is intentionally omitted — this is a 2-D projection.
 * Operators should consult the 3-D view for the full tolerance parallelepiped.
 *
 * @param center     LAB target color
 * @param thr        Per-axis threshold offsets (only .aMin/.aMax/.bMin/.bMax used)
 * @param lineColor  Stroke color
 * @param fillColor  Fill color (semi-transparent)
 */
function buildRectTrace(center: Lab, thr: Thresholds, lineColor: string, fillColor: string): object {
  const aMin = center.a + thr.aMin;
  const aMax = center.a + thr.aMax;
  const bMin = center.b + thr.bMin;
  const bMax = center.b + thr.bMax;

  // Explicitly close the rectangle by repeating the first vertex
  return {
    x: [aMin, aMax, aMax, aMin, aMin],
    y: [bMin, bMin, bMax, bMax, bMin],
    mode: 'lines',
    type: 'scatter',
    fill: 'toself',
    fillcolor: fillColor,
    line: { color: lineColor, width: 1.5 },
    hoverinfo: 'none',
  };
}
